import React from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { Dropdown } from '../../Dropdown/Dropdown';
import { GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * Iconos de banderas circulares (SVG) para una UI profesional.
 * Se utilizan rectángulos full-bleed para asegurar que rounded-full cree un círculo perfecto.
 */
const FlagIcon: React.FC<{ code: string; className?: string }> = ({ code, className = 'w-5 h-5' }) => {
  const flags: Record<string, React.ReactNode> = {
    es: (
      <svg viewBox="0 0 36 36" className={`${className} aspect-square overflow-hidden`} xmlns="http://www.w3.org/2000/svg">
        <rect fill="#FFD700" width="36" height="36" />
        <rect fill="#C60B1E" width="36" height="10" />
        <rect fill="#C60B1E" y="26" width="36" height="10" />
      </svg>
    ),
    en: (
      <svg viewBox="0 0 36 36" className={`${className} aspect-square overflow-hidden`} xmlns="http://www.w3.org/2000/svg">
        <rect fill="#00247D" width="36" height="36" />
        <path fill="#FFF" d="M36 15H21V0h-6v15H0v6h15v15h6V21h15z" />
        <path fill="#CF142B" d="M36 17H19V0h-2v17H0v2h17v17h2V19h15z" />
        <path fill="#FFF" d="M0 0l13.5 9V5.5L4.5 0H0zm22.5 0L36 9V5.5L27 0h-4.5zM0 36l13.5-9v3.5L4.5 36H0zm22.5 0L36 27v3.5L27 36h-4.5z" />
        <path fill="#CF142B" d="M0 0l12 8V6L3 0H0zm24 0L36 8V6L27 0h-3zM0 36l12-8v2L3 36H0zm24 0L36 28v2L27 36h-3z" />
      </svg>
    ),
    pt: (
      <svg viewBox="0 0 36 36" className={`${className} aspect-square overflow-hidden`} xmlns="http://www.w3.org/2000/svg">
        <rect fill="#E42518" width="36" height="36" />
        <rect fill="#046A38" width="14" height="36" />
        <circle fill="#FFD700" cx="14" cy="18" r="6" />
      </svg>
    ),
  };

  return flags[code] || <GlobeAltIcon className={className} />;
};

export interface LanguageSelectorProps {
  /** Variante visual del selector */
  variant?: 'default' | 'minimalist' | 'menu-item';
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * Componente Selector de Idioma Profesional (Siesa UI Kit)
 *
 * Implementa una arquitectura limpia y pixel-perfect con soporte para
 * una variante minimalista que solo muestra la bandera y una variante
 * de item de menú para integración en dropdowns.
 *
 * @see src/components/LanguageManagement/README.md
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'default',
  className = '',
}) => {
  const { currentLanguage, languages: ctxLanguages, setLanguage, isLanguageChanging } = useLanguage();

  // Fallback de seguridad: Si por alguna razón la API o el caché fallan y languages está vacío,
  // proveemos un arreglo mínimo funcional para evitar items "fantasma" y valores undefined.
  const safeLanguages = ctxLanguages && ctxLanguages.length > 0 ? ctxLanguages : [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' }
  ];

  // Helper para obtener el código y nombre del idioma de forma segura
  // Soporta la estructura original (codigo, nombre) y la de la API real (code, name)
  const getLangId = (l: any) => l.codigo || l.code || '';
  const getLangName = (l: any) => l.nombre || l.nativeName || l.name || '';

  const selectedLanguage = safeLanguages.find((l) => getLangId(l) === currentLanguage);

  // Mapear los idiomas a items del Dropdown con iconos de banderas
  const dropdownItems = safeLanguages.map((lang) => {
    const langId = getLangId(lang);
    const langName = getLangName(lang);
    
    return {
      children: langName,
      onClick: () => setLanguage(langId),
      selected: langId === currentLanguage,
      disabled: isLanguageChanging,
      icon: <FlagIcon code={langId} className="w-4 h-4 rounded-full shadow-sm" />,
    };
  });

  return (
    <Dropdown
      className={variant === 'menu-item' ? `w-full ${className}` : className}
      items={dropdownItems}
      disabled={isLanguageChanging}
      placeholder="Idioma"
      renderTrigger={(isOpen) => {
        // Variante para items de menú (ej: dentro del dropdown de usuario en Navbar)
        if (variant === 'menu-item') {
          return (
            <div
              title={`Idioma actual: ${selectedLanguage?.nombre || currentLanguage}`}
              aria-label={`Cambiar idioma, actual: ${selectedLanguage?.nombre || currentLanguage}`}
              className={`
                flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-normal w-full text-left
                text-content-primary dark:text-dark-content-primary 
                hover:bg-bg-primary dark:hover:bg-dark-bg-secondary
                transition-all duration-150 cursor-pointer
                ${isOpen ? 'bg-bg-primary dark:bg-dark-bg-secondary' : ''}
                ${isLanguageChanging ? 'opacity-50' : ''}
              `.trim().replace(/\s+/g, ' ')}
            >
              {/* Icono de Bandera */}
              <div className="relative shrink-0 w-4 h-4">
                <FlagIcon
                  code={currentLanguage}
                  className="w-4 h-4 rounded-full shadow-sm object-cover"
                />
                {isLanguageChanging && (
                  <div className="absolute -inset-1 border border-primary-600 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              
              {/* Texto y Chevron */}
              <div className="flex-1 min-w-0">
                <div className="text-content-primary dark:text-dark-content-primary">
                  {selectedLanguage ? getLangName(selectedLanguage) : `Idioma: ${currentLanguage.toUpperCase()}`}
                </div>
              </div>

              <ChevronDownIcon
                className={`w-3.5 h-3.5 text-content-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          );
        }

        // Variantes estándar (default y minimalist)
        return (
          <button
            type="button"
            title={`Idioma actual: ${selectedLanguage ? getLangName(selectedLanguage) : currentLanguage}`}
            aria-label={`Cambiar idioma, actual: ${selectedLanguage ? getLangName(selectedLanguage) : currentLanguage}`}
            className={`
              relative inline-flex items-center justify-center transition-all duration-200
              ${
                variant === 'minimalist'
                  ? 'w-10 h-10 rounded-full hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'
                  : 'px-3 py-1.5 rounded-lg border border-border-primary bg-white text-content-primary hover:bg-background-secondary dark:bg-dark-bg-primary dark:text-dark-content-primary dark:border-dark-border-primary dark:hover:bg-dark-bg-secondary gap-2'
              }
              ${isOpen ? 'ring-2 ring-primary-400 dark:ring-dark-border-custom' : ''}
              ${isLanguageChanging ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {/* Icono de Bandera Principal */}
            <div className="relative">
              <FlagIcon
                code={currentLanguage}
                className={`${variant === 'minimalist' ? 'w-6 h-6' : 'w-4 h-4'} rounded-full shadow-sm object-cover`}
              />
              {isLanguageChanging && (
                <div className="absolute -inset-1 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            {/* Texto (Solo en versión default) */}
            {variant === 'default' && (
              <>
                <span className="text-sm font-medium">
                  {selectedLanguage ? getLangName(selectedLanguage) : currentLanguage.toUpperCase()}
                </span>
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 text-content-tertiary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </>
            )}

            {/* Indicador sutil para minimalist */}
            {variant === 'minimalist' && !isLanguageChanging && (
              <div className="absolute bottom-1 right-1 w-2 h-2 bg-white dark:bg-dark-bg-primary rounded-full border border-border-primary dark:border-dark-border-primary flex items-center justify-center">
                <div className="w-1 h-1 bg-content-tertiary rounded-full" />
              </div>
            )}
          </button>
        );
      }}
    />
  );
};
